'use client';

import {
	type AnchorHTMLAttributes,
	type ButtonHTMLAttributes,
	forwardRef,
	type PropsWithChildren,
	type ReactElement,
	type ReactNode,
	useState,
} from 'react';
import { tv } from '../../_lib/utils';
import { Anchor, type AnchorProps } from '../../Anchor';
import { Popup, type PopupProps } from '../../Popup';
import { IconSelection } from '../../IconSelection';
import { CalendlyCtaActionPopup, type CtaAction, HubspotFormCtaActionPopup, MultipleChoiceCtaActionPopup } from '../../CtaActions';
import { IconResolver } from '../../IconResolver';

export const btnStyles = tv({
	slots: {
		base: 'items-center rounded-full inline-flex gap-3 justify-center text-left no-underline transition-all duration-slow ease-smooth cursor-pointer [&_svg]:h-auto disabled:cursor-not-allowed disabled:bg-grey-20 disabled:text-grey-60 disabled:stroke-grey-60 disabled:before:hidden',
		loader: 'animate-spin border-[0.125rem] border-solid border-[transparent] border-t-[currentColor] rounded-full h-4 inline-block w-4',
		iconOnlyWrapper:
			'h-[200%] w-full absolute top-0 left-0 -translate-y-1/2 transition-transform duration-700 ease-smooth [&>div]:h-1/2 [&>div]:w-full [&>div]:flex [&>div]:items-center [&>div]:justify-center hover:translate-y-0',
	},
	variants: {
		type: {
			primary: {
				base: 'text-button-primary-foreground! bg-button-primary-background hover:bg-button-primary-background-hover',
			},
			'primary-talk': {
				base: 'text-button-primary-foreground! bg-button-primary-background hover:bg-button-primary-background-hover rounded-br-none',
			},
			secondary: {
				base: 'text-button-secondary-foreground bg-button-secondary-background hover:bg-button-secondary-background-hover border border-button-secondary-border',
			},
			tertiary: {
				base: 'text-button-tertiary-foreground hover:bg-button-tertiary-background-hover border border-button-tertiary-border',
			},
			ghost: {
				base: 'text-button-ghost-foreground hover:bg-button-ghost-background-hover',
			},
			'inline-link': {
				base: 'text-button-secondary-foreground px-0! rounded-none hover:opacity-80',
			},
		},
		size: {
			xSmall: {
				base: '[&_svg]:w-[0.625rem] text-button-xs',
			},
			small: {
				base: '[&_svg]:w-3 text-button-sm',
			},
			medium: {
				base: '[&_svg]:w-[0.875rem] text-button-md',
			},
			large: {
				base: '[&_svg]:w-[0.875rem] text-button-md',
			},
		},
		iconOnly: {
			true: {
				base: 'overflow-hidden',
				iconOnlyWrapper: '',
			},
			false: {
				base: '',
			},
		},
		isSafari: {
			true: {},
			false: {},
		},
		theme: {
			light: {},
			dark: {},
		},
	},
	compoundVariants: [
		{
			size: 'xSmall',
			iconOnly: false,
			className: 'px-3 py-[0.5625rem]',
		},
		{
			size: 'xSmall',
			iconOnly: true,
			className: 'p-[0.5625rem]',
		},
		{
			size: 'small',
			iconOnly: false,
			className: 'px-4 py-[0.625rem]',
		},
		{
			size: 'small',
			iconOnly: true,
			className: 'p-[0.625rem]',
		},
		{
			size: 'medium',
			iconOnly: false,
			className: 'px-5 py-[0.8125rem]',
		},
		{
			size: 'medium',
			iconOnly: true,
			className: 'p-[0.8125rem]',
		},
		{
			size: 'large',
			iconOnly: false,
			className: 'px-6 py-[1.0625rem]',
		},
		{
			size: 'large',
			iconOnly: true,
			className: 'p-[1.0625rem]',
		},
	],
});

export type ButtonProps = {
	animation?: 'down';
	className?: string;
	iconOnly?: boolean;
	iconLeft?: ReactElement;
	iconRight?: ReactElement;
	disabled?: boolean;
	isLoading?: boolean;
	styleType?: 'primary' | 'primary-talk' | 'secondary' | 'tertiary' | 'ghost' | 'inline-link';
	styleSize?: 'xSmall' | 'small' | 'medium' | 'large';
	customCursorText?: string;
	parent: string;
	onClick?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
};

export type ButtonTypeProps = ButtonHTMLAttributes<HTMLButtonElement> & ButtonProps;

export type MainButtonProps = {
	styleType?: ButtonProps['styleType'];
	styleSize?: ButtonProps['styleSize'];
};

export type ComponentButtonProps = {
	className?: string;
	buttonProps?: MainButtonProps;
	label?: ReactNode;
	iconLeft?: ReactElement;
	iconRight?: ReactElement;
} & (
	| { type: 'internalLink'; href: string }
	| { type: 'externalLink'; href: string }
	| { type: 'download'; originalFilename: string; url: string }
	| { type: 'modal'; brand: PopupProps['brand']; panel: ReactNode; popupLayout: NonNullable<PopupProps['layout']>; closeButton: ReactNode }
);

export const ComponentButton = (props: ComponentButtonProps) => {
	switch (props.type) {
		case 'internalLink':
			return (
				<ButtonLink
					parent='ComponentButton'
					className={props.className}
					href={props.href}
					iconLeft={props.iconLeft}
					iconRight={props.iconRight}
					{...props.buttonProps}
				>
					{props.label}
				</ButtonLink>
			);
		case 'externalLink':
			return (
				<ButtonLink
					parent='ComponentButton'
					className={props.className}
					href={props.href}
					iconLeft={props.iconLeft}
					iconRight={props.iconRight ?? <IconResolver icon='arrow-square-out' className='w-3.5 h-3.5' />}
					{...props.buttonProps}
				>
					{props.label}
				</ButtonLink>
			);
		case 'download':
			return (
				<ButtonLink
					parent='ComponentButton'
					className={props.className}
					href={props.url}
					download={props.originalFilename}
					target='_blank'
					iconLeft={props.iconLeft}
					iconRight={props.iconRight ?? <IconResolver icon='download-simple' className='w-3.5 h-3.5' />}
					{...props.buttonProps}
				>
					{props.label}
				</ButtonLink>
			);
		case 'modal':
			return (
				<Popup
					brand={props.brand}
					layout={props.popupLayout}
					triggerButton={
						<Button
							parent='ComponentButton'
							className={props.className}
							iconLeft={props.iconLeft}
							iconRight={props.iconRight ?? <IconResolver icon='plus' className='w-3.5 h-3.5' />}
							{...props.buttonProps}
						>
							{props.label}
						</Button>
					}
					closeButton={props.closeButton}
				>
					{props.panel}
				</Popup>
			);

		default:
			return null;
	}
};

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonTypeProps>>(function Button(props, ref) {
	const { iconOnly, iconLeft, iconRight, isLoading, styleType, styleSize, ...attr } = props;
	const isIconOnly = !!iconOnly;

	const type = styleType ?? 'primary';
	const size = styleSize ?? 'large';
	const { base, loader, iconOnlyWrapper } = btnStyles({ type, iconOnly: isIconOnly, size });

	return (
		<button
			{...attr}
			disabled={props.disabled}
			ref={ref}
			className={base({ className: props.className })}
			onClick={e => {
				props.onClick && props.onClick(e);
			}}
		>
			{iconLeft}
			{isLoading ? (
				<>
					<div className={loader()} />
					Loading
				</>
			) : (
				<>
					{isIconOnly && props.animation ? (
						<div className={iconOnlyWrapper()}>
							<div>{props.children}</div>
							<div>{props.children}</div>
						</div>
					) : (
						<div>{props.children}</div>
					)}
				</>
			)}
			{iconRight}
		</button>
	);
});

export type ButtonLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
	href: AnchorProps['href'];
	parent: string;
} & ButtonProps;

export const ButtonLink = forwardRef<HTMLAnchorElement, PropsWithChildren<ButtonLinkProps>>(function ButtonLink(props, ref) {
	const { iconOnly, parent, iconLeft, iconRight, isLoading, styleType, styleSize, ...attr } = props;
	const isIconOnly = !!iconOnly;
	const type = styleType ?? 'primary';
	const size = styleSize ?? 'medium';
	const { base, loader } = btnStyles({ type, iconOnly: isIconOnly, size });

	return (
		<Anchor
			{...attr}
			className={base({ className: props.className })}
			ref={ref}
			onClick={e => {
				props.onClick && props.onClick(e);
			}}
			/* eslint-disable-next-line react/no-children-prop */
			children={
				<>
					{iconLeft}
					{isLoading ? (
						<>
							<div className={loader()} />
							Loading
						</>
					) : (
						<div>{props.children}</div>
					)}
					{iconRight}
				</>
			}
		/>
	);
});

interface ButtonActionCommonProps {
	popupContent?: ReactNode;
	type?: 'link' | 'contentPopup' | 'videoPopup';
}

type ButtonActionConditionalProps = ButtonProps | ButtonLinkProps;

export type ButtonActionProps = ButtonActionCommonProps & ButtonActionConditionalProps;

export const ButtonAction = forwardRef<HTMLAnchorElement | HTMLButtonElement, PropsWithChildren<ButtonActionProps>>(
	function ButtonAction(props, ref) {
		const { popupContent, type: buttonType, ...buttonProps } = props;
		const type = buttonType ?? 'link';

		return (
			<>
				{type === 'link' && <ButtonLink {...buttonProps} />}
				{type === 'contentPopup' && props.popupContent && (
					<Popup
						layout={type === 'contentPopup' ? 'sidebar' : 'video'}
						triggerButton={<Button {...buttonProps} iconRight={props.iconRight ?? <IconSelection icon='plusCircle' />} />}
					>
						{props.popupContent}
					</Popup>
				)}
				{type === 'videoPopup' && props.popupContent && (
					<Popup
						layout={type === 'contentPopup' ? 'sidebar' : 'video'}
						triggerButton={<Button {...buttonProps} iconRight={props.iconRight ?? <IconSelection icon='play' />} />}
					>
						{props.popupContent}
					</Popup>
				)}
			</>
		);
	},
);

export type ButtonCtaActionProps = {
	ctaAction?: CtaAction;
	label?: string;
	buttonProps?: MainButtonProps;
	unstyled?: boolean;
};

export const ButtonCtaAction = (props: ButtonCtaActionProps) => {
	const [open, setOpen] = useState(false);

	if (props.ctaAction === undefined || props.label === undefined) {
		return null;
	}

	if (props.ctaAction.type === 'link') {
		// Link

		if (props.unstyled) {
			return <Anchor href={props.ctaAction.href}>{props.label}</Anchor>;
		}

		return (
			<ButtonLink parent='ButtonCtaAction' href={props.ctaAction.href} {...props.buttonProps}>
				{props.label}
			</ButtonLink>
		);
	} else {
		// Popup
		return (
			<>
				{props.unstyled ? (
					<button
						onClick={() => {
							setOpen(true);
						}}
					>
						{props.label}
					</button>
				) : (
					<Button
						parent='ButtonCtaAction'
						onClick={() => {
							setOpen(true);
						}}
						{...props.buttonProps}
					>
						{props.label}
					</Button>
				)}

				{props.ctaAction.type === 'hubspotForm' && <HubspotFormCtaActionPopup open={open} setOpen={setOpen} {...props.ctaAction} />}
				{props.ctaAction.type === 'calendly' && <CalendlyCtaActionPopup open={open} setOpen={setOpen} {...props.ctaAction} />}
				{props.ctaAction.type === 'multiple-choice' && <MultipleChoiceCtaActionPopup open={open} setOpen={setOpen} {...props.ctaAction} />}
			</>
		);
	}
};
